import React, { PropsWithChildren, useEffect, useState } from "react";
import { SubscriptionPaymentMethod } from "@shared/features/settings/subscription/types";
import {
  PaymentMethod,
  PaymentType,
  PromoCodePlansPricesType,
  UsageType,
} from "@shared/types";
import { usePurchaseSubscriptionQuery } from "features/settings/common/hooks";
import { getAnalyticsInvoiceType } from "features/settings/common/utils";
import useMedia from "utils/hooks/useMedia";
import {
  DRAWER_CHECKOUT_WIDTH,
  IS_APPLE_PAY,
  IS_GOOGLE_PAY,
  PROMO_CODE_QUERY_PARAM,
} from "features/settings/common/constants";
import useQuery from "utils/hooks/useQuery.ts";
import { useUser } from "@shared/utils/hooks/misc.ts";
import { useUsageLimits } from "@shared/features/settings/hooks.ts";
import { useDisplayPromoCode } from "features/settings/Subscription/hooks.ts";
import useGetUpgradePrices from "features/settings/common/useGetUpgradePrices.ts";
import { sharedLib } from "@shared/services/shared.service.ts";
import { LOCAL_STORAGE_FAILED_PAYMENT_KEY } from "common/constants.ts";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import { SUBSCRIPTION_STEPS } from "@shared/features/settings/subscription/types";
import { getIsDifferentPaymentMethod } from "features/settings/Subscription/utils.tsx";

interface BuySubscriptionProviderProps extends PropsWithChildren {
  isStandaloneApiSlots?: boolean;
}

const BuySubscriptionProvider = ({
  children,
  isStandaloneApiSlots,
}: BuySubscriptionProviderProps) => {
  const {
    subscriptionParams,
    setDuration,
    setSubscriptionType,
    setPaymentMethod: setQueryPaymentMethod,
    previousStep,
    isUpgrade,
    isExtend,
    subGroupId,
    plans,
    isFetching,
    isLoading,
  } = usePurchaseSubscriptionQuery();
  const query = useQuery();
  const { user } = useUser();
  const usePromoCode = useDisplayPromoCode({ user });
  const queryPromoCode = usePromoCode
    ? query.get(PROMO_CODE_QUERY_PARAM) || undefined
    : undefined;

  const [promoCode, setPromoCode] = useState<string | undefined>(
    queryPromoCode
  );
  const [promoCodePrices, setPromoCodePrices] = useState<
    PromoCodePlansPricesType | undefined
  >();
  const [numApiSlots, setNumApiSlots] = useState<number | undefined>(
    isStandaloneApiSlots ? 10 : 0
  );
  const [subscriptionPaymentMethod, setSubscriptionPaymentMethod] = useState<
    SubscriptionPaymentMethod | undefined
  >();
  const [paymentMethod, setPaymentMethod] = useState<
    PaymentMethod | undefined
  >();
  const [cryptoTotalPrice, setCryptoTotalPrice] = useState<
    string | undefined
  >();

  const analyticsInvoiceType = getAnalyticsInvoiceType(
    isUpgrade,
    isExtend,
    !!subGroupId
  );

  const isBottomSummary = useMedia(
    [`(max-width: ${DRAWER_CHECKOUT_WIDTH}px)`],
    [true],
    false
  );

  const { getExtraQuota } = useUsageLimits();
  const extraClientSlots = getExtraQuota(UsageType.CLIENT);
  const shouldSetNumApiSlots =
    isExtend &&
    !isStandaloneApiSlots &&
    subscriptionParams.step === SUBSCRIPTION_STEPS.DURATION;

  useEffect(() => {
    if (shouldSetNumApiSlots) {
      setNumApiSlots(extraClientSlots ?? 0);
    }
  }, [shouldSetNumApiSlots, extraClientSlots]);

  const getPlan = () => {
    if (!plans) return undefined;
    if (subGroupId) return plans["Group"];
    if (subscriptionParams.plan) return plans[subscriptionParams.plan];
    if (isStandaloneApiSlots && user?.subscription_plan_type)
      return plans[user.subscription_plan_type];
    return undefined;
  };

  const plan = getPlan();

  const showDurationSelection = !isUpgrade || IS_APPLE_PAY || IS_GOOGLE_PAY;
  const duration =
    (isStandaloneApiSlots
      ? user?.package_duration
      : showDurationSelection
      ? subscriptionParams.duration
      : plan?.upgradePlan?.subscription_days) ?? 360;
  const additionalSlotPrice = isUpgrade
    ? plan?.upgradePlan?.additional_slot_price
    : plan?.[duration]?.additional_slot_price;

  const promoCodePriceData = promoCodePrices?.[duration];
  const isValidPromoCode =
    promoCodePriceData?.code === 200 && !promoCodePriceData?.message;

  const isGetUpgradePricesEnabled =
    (isUpgrade && !!plan) || isStandaloneApiSlots;
  const { upgradePrices, isUpgradePricesLoading } = useGetUpgradePrices({
    numApiSlots: numApiSlots ?? 0,
    enabled: isGetUpgradePricesEnabled,
    planType: isUpgrade ? plan?.subscription_plan_type : undefined,
    paymentType: isUpgrade ? PaymentType.UPGRADE : PaymentType.PRORATED_SLOTS,
    includeNumClients: !!additionalSlotPrice,
    promoCode: isValidPromoCode ? promoCode : undefined,
  });

  const {
    data: paymentMethods,
    isFetching: isFetchingPaymentMethods,
    isLoading: isLoadingPaymentMethods,
  } = sharedLib.sharedApi.settings.useGetAllPaymentMethodsQuery(undefined);

  const webviewIsDifferentPayment = getIsDifferentPaymentMethod(user);
  const showUpgradePrices =
    isUpgrade && !IS_APPLE_PAY && !webviewIsDifferentPayment;
  const isCryptoPayment =
    subscriptionPaymentMethod === SubscriptionPaymentMethod.CRYPTO;

  const isAutoRenewal =
    !isCryptoPayment &&
    !localStorage?.getItem(LOCAL_STORAGE_FAILED_PAYMENT_KEY);

  const onSetPaymentMethod = (paymentMethod?: PaymentMethod) => {
    setPaymentMethod(paymentMethod);
    if (paymentMethod) {
      setQueryPaymentMethod?.(paymentMethod.payment_option);
    }
  };

  const contextValue = {
    subscriptionParams,
    plan,
    plans,
    duration,
    setDuration,
    setSubscriptionType,
    previousStep,
    isUpgrade,
    showUpgradePrices,
    isExtend: isExtend && !isStandaloneApiSlots,
    subGroupId,
    isBottomSummary,
    analyticsInvoiceType,
    numApiSlots:
      additionalSlotPrice || isStandaloneApiSlots ? numApiSlots : undefined,
    setNumApiSlots,
    isFetching,
    isLoading,
    promoCode,
    setPromoCode,
    promoCodePrices,
    setPromoCodePrices,
    upgradePrices,
    isUpgradePricesLoading,
    isAutoRenewal,
    isStandaloneApiSlots,
    subscriptionPaymentMethod,
    setSubscriptionPaymentMethod,
    paymentMethod,
    setPaymentMethod: onSetPaymentMethod,
    cryptoTotalPrice,
    setCryptoTotalPrice,
    paymentMethods,
    isFetchingPaymentMethods,
    isLoadingPaymentMethods,
    additionalSlotPrice,
    isCryptoPayment,
    queryPromoCode,
    isPromoCodeApplied: isValidPromoCode,
    isGetUpgradePricesEnabled,
    extraClientSlots,
  };

  return (
    <BuySubscriptionContext.Provider value={contextValue}>
      {children}
    </BuySubscriptionContext.Provider>
  );
};

export default BuySubscriptionProvider;
